import { formatHoursPerDay } from "@/lib/countdown/format.ts";
import { type LegPace, PACE_OK_MAX } from "@/lib/countdown/legs.ts";
import styles from "./countdown.module.css";

const STATUS_LABEL: Record<LegPace["status"], string> = {
  ok: "On pace",
  tight: "Tight",
  infeasible: "Doesn't fit",
};

/**
 * Utilization as a bar: what this leg asks of a day against what you said a
 * day holds. The notch at 85% is where "comfortable" ends and the end of the
 * track is where the day does — so a bar that overruns its own track is the
 * whole message, before you've read a number.
 *
 * The bar is decoration (`aria-hidden`); the caption under it is the real
 * statement, and it's the same sentence a screen reader gets.
 */
export function PaceMeter({ leg }: { leg: LegPace }) {
  const fill = Number.isFinite(leg.utilization) ? Math.min(1, leg.utilization) : 1;
  const over = leg.utilization > 1;

  return (
    <div className={styles.meter}>
      <div className={styles.meterTrack} data-status={leg.status} aria-hidden="true">
        <div className={styles.meterFill} style={{ width: `${fill * 100}%` }} />
        <span className={styles.meterNotch} style={{ left: `${PACE_OK_MAX * 100}%` }} />
        {over && <span className={styles.meterOver} />}
      </div>
      <p className={styles.meterCaption}>
        <span className={styles.pill} data-status={leg.status}>
          {STATUS_LABEL[leg.status]}
        </span>
        {/* Its own element so the pinned bar can drop the numbers and keep the
            verdict when a phone doesn't have room for both. */}
        <span className={styles.meterNumbers}>
          {leg.complete
            ? "nothing left in this leg"
            : `${formatHoursPerDay(leg.requiredHoursPerDay)} h/day needed · ${formatHoursPerDay(
                leg.availableHoursPerDay,
              )} h/day budgeted`}
        </span>
      </p>
    </div>
  );
}
